import { ApolloClient } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import { Duration } from 'luxon'
import { NextPage } from 'next'
import { Case, Default, Switch } from 'react-if'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { publisher, releasedEvent } from '@/constants/seo'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { Seo, useDataForEpisode } from '@/layout/Seo'
import { MovieJsonLd, PodcastEpisodeJsonLd, TVEpisodeJsonLd } from '@/layout/Seo/JsonLd'
import { VideoObject } from '@/layout/Seo/JsonLd/types'
import { getVideoObjects } from '@/layout/Seo/ProjectSeoHelper'
import { getEpisodeTitle } from '@/layout/Seo/SeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import {
  CatalogTitle,
  fetchTitle,
  getFirstTitleRating,
  getTitleGenres,
  getWatchableTitle,
} from '@/services/ContentCatalog'
import { createEpisodeDeepLink } from '@/services/DeepLinkService'
import { ShopifyStoreConfig } from '@/services/EmailCapturePageService/EmailCapturePageService'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getParentalControlsForContent, getSkipsEnabledFromParentalControls } from '@/services/ParentalControlsService'
import { isSellingTickets } from '@/services/PhaseManagerService/PhaseManagerService'
import {
  Episode,
  Project,
  ProjectMeta,
  getContentEpisode,
  getEpisode,
  getProjectAbout,
} from '@/services/ProjectsService'
import { findSlugByEpisodeGuid } from '@/services/ProjectsService/FriendlyEpisodeSlugHelper'
import { ContentEpisode } from '@/services/ProjectsService/types'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService'
import { buildDecorativeEpisodePath } from '@/utils/EpisodeUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { redact } from '@/utils/sensitive-data'
import { shouldHideEpisodeFromSearchEngines, shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PerfLogsProvider } from '@/utils/usePerfLogs'
import { WatchEpisodeView } from '@/views/WatchEpisodeView'

interface WatchEpisodePageProps {
  deepLink: string | null
  episode: Episode
  hasPayItForward: boolean
  locale: string
  project: Project
  shopifyStore?: ShopifyStoreConfig
  projectTitle: CatalogTitle | null
  watchableTitle: CatalogTitle | null
  isGuildMember?: boolean
  isSellingTickets: boolean
  canViewUnauthed?: boolean
  experiments: ServerExperiments
  userId?: string | null
  skipsEnabled?: boolean
  contentEpisode?: ContentEpisode
}

export const WatchEpisodePage: NextPage<WatchEpisodePageProps & ProjectMeta> = (props) => {
  const {
    episode,
    project,
    hasPayItForward,
    projectTitle,
    watchableTitle,
    isGuildMember,
    isSellingTickets,
    canViewUnauthed,
    userId,
    shopifyStore,
    deepLink,
    experiments,
    locale,
    skipsEnabled,
    contentEpisode,
  } = props
  const { meta } = useDataForEpisode(
    {
      logoCloudinaryPath: project.logoCloudinaryPath,
      name: project.name,
      projectType: project.projectType,
      slug: project.slug,
    },
    episode,
    locale,
    contentEpisode,
  )
  const url = meta.canonical
  const duration = contentEpisode?.duration ?? episode?.source?.duration ?? 0
  const description = contentEpisode?.description ?? episode.description
  const name = contentEpisode?.name ?? episode.subtitle
  const thumbnailUrl = meta?.openGraph?.images?.[0]?.url
  const releaseDate = contentEpisode?.originalReleaseDate ?? episode.releaseDate
  const contentRating = getFirstTitleRating(project?.title)
  const genres = getTitleGenres(watchableTitle)
  const episodeNumber =
    contentEpisode && 'episodeNumber' in contentEpisode ? contentEpisode.episodeNumber : episode.episodeNumber
  const seasonNumber =
    contentEpisode && 'season' in contentEpisode ? contentEpisode?.season?.seasonNumber : episode.seasonNumber
  const video: VideoObject = {
    type: 'VideoObject',
    contentUrl: url,
    description,
    duration: duration
      ? Duration.fromDurationLike({ hour: 0, minute: 0, second: duration }).normalize().toISO()
      : undefined,
    name,
    thumbnailUrl,
    uploadDate: releaseDate || '2021-03-25T12:00:00Z',
  }

  const shouldHide = Boolean(shouldHideEpisodeFromSearchEngines(episode) || shouldHideProjectFromSearchEngines(project))
  return (
    <>
      <Seo
        {...meta}
        friendlyPath={buildDecorativeEpisodePath(
          {
            seasonNumber,
            episodeNumber,
            subtitle: episode.subtitle,
          },
          { disableDecoration: false },
        )}
        nofollow={shouldHide}
        noindex={shouldHide}
        path={`/watch/${project.slug}/episode/${episode.guid}`}
      />
      <Switch>
        <Case condition={project.projectType === projectTypes.movie}>
          <MovieJsonLd
            contentRating={contentRating}
            dateCreated={releaseDate}
            description={description}
            genre={genres ?? []}
            image={thumbnailUrl}
            name={getEpisodeTitle({ project, episode })}
            publisher={publisher}
            releasedEvent={releasedEvent}
            trailer={getVideoObjects(project.videos)}
            url={url}
            video={video}
          />
        </Case>
        <Case condition={project.projectType === projectTypes.podcast}>
          <PodcastEpisodeJsonLd
            contentRating={contentRating}
            description={description}
            episodeNumber={episodeNumber}
            genre={genres ?? []}
            image={thumbnailUrl}
            name={getEpisodeTitle({ project, episode })}
            partOfSeries={{
              type: 'CreativeWorkSeries',
              description: watchableTitle?.description?.medium || watchableTitle?.description?.short || '',
              name: project.name,
              url: `${paths.getBaseURL()}${paths.watch.index}/${episode.projectSlug}`,
            }}
            publisher={publisher}
            releasedEvent={releasedEvent}
            url={url}
            video={video}
          />
        </Case>
        <Default>
          <TVEpisodeJsonLd
            contentRating={contentRating}
            description={description}
            episodeNumber={episode.episodeNumber}
            genre={genres ?? []}
            image={thumbnailUrl}
            name={getEpisodeTitle({ project, episode })}
            partOfSeason={{
              type: 'TVSeason',
              name: `${project.name} - Season ${seasonNumber}`,
              seasonNumber,
            }}
            partOfSeries={{
              type: 'TVSeries',
              description: watchableTitle?.description?.medium || watchableTitle?.description?.short || '',
              name: project.name,
              url: `${paths.getBaseURL()}${paths.watch.index}/${episode.projectSlug}`,
            }}
            publisher={publisher}
            releasedEvent={releasedEvent}
            url={url}
            video={video}
          />
        </Default>
      </Switch>
      <PerfLogsProvider>
        <GetAppBannerProvider deepLink={deepLink} projectSlug={project.slug} context="episode">
          <ProjectMetaProvider project={props}>
            <WatchEpisodeView
              projectType={project.projectType}
              hasPayItForward={hasPayItForward}
              episodeId={contentEpisode?.id ?? episode.guid}
              projectSlug={contentEpisode?.project?.slug ?? episode.projectSlug}
              projectName={project.name}
              episode={episode}
              skipAds={!!episode?.isTrailerM3u8}
              shopifyStore={shopifyStore}
              projectTitle={projectTitle}
              watchableTitle={watchableTitle}
              isGuildMember={isGuildMember}
              isSellingTickets={isSellingTickets}
              canViewUnauthed={canViewUnauthed}
              userId={userId}
              showParentalControlsExperiment={
                project.slug === slugs.sketch ? true : Boolean(experiments['family-friendly-audio-support']) ?? false
              }
              skipsEnabled={skipsEnabled}
              contentEpisode={contentEpisode}
              autoplay
            />
          </ProjectMetaProvider>
        </GetAppBannerProvider>
      </PerfLogsProvider>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<WatchEpisodePageProps>(
  async ({ federationClient, locale, next404, nextRedirect, params, preview, req, res, resolvedUrl, userId }) => {
    const projectSlug = getItemOrFirstEntry(params.project)
    if (!projectSlug) return next404()
    if (projectSlug === slugs.theChosen) return nextRedirect(paths.watch.index)

    const projectWatchPath = `${paths.watch.index}/${params.project}`

    const pathParts = params.friendlyPath
    if (!Array.isArray(pathParts)) return next404()

    const guid = pathParts[0]
    if (!guid) {
      logger().warn(`No episode guid provided`, { projectSlug, pathParts })
      return nextRedirect(projectWatchPath)
    }

    if (projectSlug === slugs.dryBar) {
      const correctSlug = await findSlugByEpisodeGuid(federationClient, guid)
      if (correctSlug && correctSlug !== projectSlug) {
        return nextRedirect(resolvedUrl.replace(projectSlug, correctSlug))
      }
    }

    const [episodeResponse, contentEpisode, guildStatus, parentalControls] = await Promise.all([
      getEpisode({ guid, projectSlug }, federationClient),
      getContentEpisode({ guid }, federationClient),
      getGuildStatus(federationClient),
      getParentalControlsForContent({ client: federationClient, contentId: guid }),
    ])

    if (!episodeResponse || !episodeResponse.episode || !episodeResponse.project) {
      const ipAddress = req.socket.remoteAddress
      const referer = req.headers['referer']
      const context = { guid, projectSlug, episodeResponse, ipAddress, referer }
      logger().error(`Episode not found for guid=${guid} and projectSlug=${projectSlug}`, context)

      return nextRedirect(projectWatchPath)
    }

    const skipsEnabled = getSkipsEnabledFromParentalControls(parentalControls?.getContent)

    const path = `${paths.watch.index}/${projectSlug}/episode/${guid}`
    // Grab all Dry Bar Merch for Specials
    const shopifyUrl = contentEpisode?.episode?.__typename === 'ContentSpecial' ? slugs.dryBar : projectSlug

    const [metaAndTitles, shopifyStore, deepLink, experiments, translations] = await Promise.all([
      getMetaAndTitles({ projectSlug, locale, preview, federationClient, episode: episodeResponse.episode }),
      getShopifyConfigurationForProject(shopifyUrl, { locale, preview }),
      createEpisodeDeepLink(
        {
          path,
          projectSlug,
          guid,
          projectName: contentEpisode?.projectName ?? episodeResponse?.project?.name,
          contentName: contentEpisode?.episode?.name ?? episodeResponse?.episode?.subtitle,
          audioLocale: locale,
          captionLocale: locale,
        },
        {
          campaign: 'prompt-app-install',
          feature: 'app-prompt',
          channel: 'angel-web',
        },
      ),
      runExperiments({ req, res, skipsEnabled, projectSlug }),
      loadTranslations(locale, ['common', 'watch', 'app-promo', 'ads', 'account', 'guild']),
    ])

    const canViewUnauthed = getCanViewUnauthed(userId, contentEpisode?.episode)
    const hasPayItForward = getHasPayItForward(contentEpisode)
    const { projectMeta, projectTitle, watchableTitle } = metaAndTitles
    if (!projectMeta) {
      logger().warn(`No project meta found for projectSlug=${projectSlug}`, { projectSlug, locale, preview })
      return next404()
    }

    return {
      props: {
        deepLink,
        hasPayItForward,
        locale,
        shopifyStore,
        projectTitle: omitUndefineds(projectTitle) || null,
        watchableTitle: omitUndefineds(watchableTitle) || null,
        isGuildMember: guildStatus?.isGuildMember ?? false,
        canViewUnauthed,
        episode: episodeResponse.episode,
        project: episodeResponse.project,
        contentEpisode: contentEpisode?.episode,
        userId: userId ?? null,
        // DESIGN NOTE - break out derived value so that we don't leak unintentional flow phase info
        isSellingTickets: isSellingTickets(projectMeta.primaryFlowPhases ?? []),
        skipsEnabled,
        experiments,
        ...redact(projectMeta, ['primaryFlowPhases']),
        ...translations,
      },
    }
  },
)

interface GetMetaAndTitlesParams {
  projectSlug: string
  locale: string
  preview: boolean
  federationClient: ApolloClient<object>
  episode: Episode
}

async function getMetaAndTitles({ projectSlug, locale, preview, federationClient, episode }: GetMetaAndTitlesParams) {
  const projectMeta = await getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview })
  const projectTitle = await fetchTitle(locale, projectMeta?.title?.id ?? projectMeta?.contentCatalogId)
  const watchableTitle = await getWatchableTitle(locale, projectTitle, episode)

  return { projectMeta, projectTitle, watchableTitle }
}
export default WatchEpisodePage

async function runExperiments({
  req,
  res,
  skipsEnabled,
  projectSlug,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  skipsEnabled: boolean
  projectSlug: string
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  // Add family-friendly audio support experiment if skips are enabled
  if (skipsEnabled && projectSlug !== slugs.sketch) {
    expConfigs.push({ experimentKey: 'family-friendly-audio-support', defaultValue: false })
  }

  const experiments = await runExperimentsOnServer(req, res, expConfigs)

  return experiments
}

export type PifEnabledEntity = { pifEnabled?: boolean | null }

export function getHasPayItForward(entity?: PifEnabledEntity | null): boolean {
  return Boolean(entity?.pifEnabled)
}

const FREE_UNAUTH_EPISODE_IDS = new Set<string>(['75c66550-f8fd-480a-8664-435cfffcfbf4'])

export function getCanViewUnauthed(
  userId: string | null | undefined,
  episode?: { __typename?: string | null; id?: string | null } | null,
): boolean {
  if (userId) return false
  const isSneakPeek = episode?.__typename === 'ContentSneakPeek'
  const isAllowlisted = episode?.id ? FREE_UNAUTH_EPISODE_IDS.has(episode.id) : false
  return isSneakPeek || isAllowlisted
}
